import { useState } from 'react';

import { EmployeeObjectivesList } from './EmployeeObjectivesList';
import { ObjectivesMetricTiles } from './ObjectivesMetricTiles';
import { ObjectivesOverallProgression } from './ObjectivesOverallProgression';

import type { ObjectivesOverviewDensity } from './objectivesOverviewDensity';
import type { Objective } from '@/types/objectives';
import type { ObjectivesSummary } from '@/utils/objectivesSummary';

import { useObjectiveOwners } from '@/hooks/objectives/useObjectiveOwners';

interface EmployeeObjectivesOverviewProps {
    objectives: Objective[];
    summary: ObjectivesSummary;
    /** False until the figures are backed by a server answer: they show as dashes, never zeros. */
    isSummaryKnown: boolean;
    isLoadingObjectives: boolean;
    /**
     * Identifies the window the objectives were read in. When it changes, the list goes back to its
     * first page: page four of the previous period does not exist in the next one.
     */
    windowKey: string;
    /** Objective cards without their menu: a click opens the objective, and nothing else. */
    isReadOnly?: boolean;
    /**
     * How tightly the progression and the tiles are drawn. The default is the scale the profile's
     * Objectives tab reads them at; a screen that gives the block less room asks for `compact`.
     */
    density?: ObjectivesOverviewDensity;
    dataTestId?: string;
}

/**
 * An employee's objectives in a window: the overall progression, four headline tiles and the
 * paginated list of the objectives themselves, each a link to its detail. Shared by the profile's
 * Objectives tab and the manager's evaluation page, so the same objectives read the same way in
 * both -- at the scale each of those screens asks for.
 */
export const EmployeeObjectivesOverview = ({
    objectives,
    summary,
    isSummaryKnown,
    isLoadingObjectives,
    windowKey,
    isReadOnly = false,
    density = 'default',
    dataTestId = 'employee-objectives-overview',
}: EmployeeObjectivesOverviewProps) => {
    // The page number is tied to the window it was counted in, rather than reset inside a picker
    // handler: the window can move from elsewhere on the page -- the assessment tab's objectives
    // card, the evaluation period above the block -- and a reset written in a handler would never
    // run, leaving the reader on page four of a period that has three.
    const [pagination, setPagination] = useState({ windowKey, page: 1 });
    const page = pagination.windowKey === windowKey ? pagination.page : 1;
    const setPage = (nextPage: number) => setPagination({ windowKey, page: nextPage });

    // The shared card draws the owner and participant avatars, so they are resolved the same way
    // the objectives pages do -- one batched directory call, and the same owner-plus-participants
    // rule, rather than a second way of collecting the same ids. No search here.
    const { employeesById, isLoading: areEmployeesLoading } = useObjectiveOwners(objectives, '');

    return (
        // The block owns the rhythm between its three bands, so the profile tab and the
        // evaluation page cannot drift apart -- and so one line changes it everywhere.
        // `data-density` states the scale it was asked for: the classes below say how a scale
        // looks, and this says which one a screen got, which is what must not drift.
        <div
            className={'flex flex-col gap-8 md:gap-12'}
            data-density={density}
            data-testid={dataTestId}
        >
            <ObjectivesOverallProgression
                percentage={summary.overall}
                objectiveCount={summary.total}
                isLoading={!isSummaryKnown}
                density={density}
            />

            <ObjectivesMetricTiles
                summary={summary}
                isLoading={!isSummaryKnown}
                density={density}
            />

            <EmployeeObjectivesList
                objectives={objectives}
                employeesById={employeesById}
                isLoading={isLoadingObjectives || areEmployeesLoading}
                page={page}
                onPageChange={setPage}
                isReadOnly={isReadOnly}
            />
        </div>
    );
};
